import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../../util/reactIntl';

import { ExternalLink, IconArrowHead, NamedLink } from '../../../../../components';

import css from './PriorityLinks.module.css';

/**
 * Create component that shows only a single "Post a new listing" link.
 *
 * @param {*} props contains customLinksMenuClass
 * @returns div with only one link inside.
 */
export const CreateListingMenuLink = props => {
  const { customLinksMenuClass, loggedOutCta } = props;
  return (
    <div className={customLinksMenuClass}>
      <NamedLink
        name="NewListingPage"
        className={classNames(css.priorityLink, css.highlight, {
          [css.becomeHostLink]: loggedOutCta,
        })}
      >
        <span className={css.priorityLinkLabel}>
          <FormattedMessage
            id={loggedOutCta ? 'TopbarDesktop.becomeAHost' : 'TopbarDesktop.createListing'}
          />
        </span>
        {loggedOutCta ? (
          <IconArrowHead direction="right" size="small" rootClassName={css.becomeHostIcon} />
        ) : null}
      </NamedLink>
    </div>
  );
};

/**
 * Link component that can be used on TopbarDesktop.
 *
 * @param {*} props containing linkConfig including resolved 'route' params for NamedLink.
 * @returns NamedLink or ExternalLink component based on config.
 */
const PriorityLink = ({ linkConfig, loggedOutCta }) => {
  const { text, type, href, route, highlight } = linkConfig;
  const isBecomeHostCta = loggedOutCta && highlight;
  const classes = classNames(css.priorityLink, {
    [css.highlight]: highlight,
    [css.becomeHostLink]: isBecomeHostCta,
  });
  const id = `priority-link-${text.toLowerCase().replace(/ /g, '-')}`;

  const label = (
    <>
      <span className={css.priorityLinkLabel}>{text}</span>
      {isBecomeHostCta ? (
        <IconArrowHead direction="right" size="small" rootClassName={css.becomeHostIcon} />
      ) : null}
    </>
  );

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    return (
      <NamedLink name={name} params={params} to={to} className={classes} id={id}>
        {label}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={classes}>
      {label}
    </ExternalLink>
  );
};

/**
 * Create priority links, which are visible on the desktop layout on the Topbar.
 * If space is limited, this doesn't include anything to the Topbar.
 *
 * @param {*} props contains links array and setLinks function
 * @returns list of priority links.
 */
const PriorityLinks = props => {
  const containerRef = useRef(null);

  // With this useEffect, we measure the widths of each rendered priority link
  // This is done before the real rendering and it's done outside the viewport.
  // Re-run when links are reset without widths (e.g. create-listing visibility change).
  useEffect(() => {
    const isMeasured = props.links?.[0]?.width;
    if (containerRef.current && props.links?.length > 0 && !isMeasured) {
      const linksFromRenderedWrapper = [...containerRef.current.childNodes];
      let cumulatedWidth = 0;
      // Generate an array of link configs with width & cumulatedWidth included
      const linksWithWidths = props.links.reduce((links, l, i) => {
        const width = linksFromRenderedWrapper[i].offsetWidth;
        cumulatedWidth = cumulatedWidth + width;
        return [...links, { ...l, width, cumulatedWidth }];
      }, []);
      props.setLinks(linksWithWidths);
    }
  }, [props.links, props.setLinks]);

  const { links, priorityLinks, loggedOutCta } = props;
  const isMeasured = links?.[0]?.width && (priorityLinks.length === 0 || priorityLinks?.[0]?.width);
  const styleWrapper = !!isMeasured
    ? {}
    : {
        style: {
          position: 'absolute',
          top: '-2000px',
          left: '-2000px',
          width: '100%',
          height: 'var(--topbarHeightDesktop)',
          display: 'flex',
          flexDirection: 'row',
        },
      };
  const linkConfigs = isMeasured ? priorityLinks : links;

  // Always render inline in the Topbar tree so SSR and the initial client render match.
  // (Portaling to document.body caused hydration mismatches.)
  return (
    <ul className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
      {linkConfigs.map((linkConfig, index) => {
        return (
          <li key={`${linkConfig.text}_${index}`} className={css.priorityLinkItem}>
            <PriorityLink linkConfig={linkConfig} loggedOutCta={loggedOutCta} />
          </li>
        );
      })}
    </ul>
  );
};

export default PriorityLinks;
